import { useContext } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { RESOURCE_NAME } from "common/authorization";
import { OBJECT_TYPE, WORKFLOW_STEP_MANUAL_STATUS } from "common/constants";
import { arrayHasAtLeastOneItem } from "common/utils/arrays";
import {
  GetWorkflowQuery,
  WorkflowFieldsFragment,
} from "../../generated/graphql";
import { AuthContext } from "../Authorization/AuthContext";
import { FlexRow } from "../Common/__styles__/Layout";
import { Actions } from "../Common/ActionCell";
import { Icon } from "../Common/Icons/LucideIcons";
import { Tag } from "../Common/Tag";
import { ActionsProps } from "../Inputs/DropdownMenu";
import { WorkflowStepContainer } from "./__styles__/WorkflowStep";
import { useDeleteWorkflowStepModal } from "./DeleteWorkflowStepModal";
import { useInsertWorkflowStepModal } from "./InsertWorkflowStepModal";
import { useShareWorkflowStepModal } from "./ShareWorkflowStepModal";
import { useUpdateWorkflowStepStatusModal } from "./useUpdateWorkflowStepStatusModal";
import {
  getDerivedTag,
  getOnCallToActionClick,
  getWorkflowStepManualStatusDisplay,
  getWorkflowStepStatusDisplay,
} from "./workflowUtils";

export const WorkflowStep = ({
  workflowStep,
  isManualMode,
  objectId,
  objectType,
  workflow,
}: {
  workflowStep: WorkflowFieldsFragment["workflowSteps"][number];
  isManualMode: boolean;
  objectId?: string;
  objectType?: OBJECT_TYPE;
  workflow: NonNullable<GetWorkflowQuery["workflow"]>;
}) => {
  const { authorized, isPublic } = useContext(AuthContext);
  const history = useHistory();
  const { pathname } = useLocation();

  const derivedTagInfo = isManualMode
    ? getDerivedTag(workflowStep.manualStatus, !!workflowStep.submission)
    : null;

  const statusTagInfo = derivedTagInfo
    ? derivedTagInfo
    : isManualMode
      ? getWorkflowStepManualStatusDisplay(workflowStep.manualStatus)
      : getWorkflowStepStatusDisplay(workflowStep.status);

  const stepName = workflowStep.name;

  const onCardClick = getOnCallToActionClick({
    canCreateSubmission: workflowStep.currentUserCanCreate,
    canViewSubmission: workflowStep.currentUserCanViewResource,
    workflowStep,
    push: history.push,
    pathname,
    objectId,
    objectType,
    isManualMode,
    isPublic,
  });

  const interactionState: "clickable" | "disabled" | "default" = onCardClick
    ? "clickable"
    : isManualMode
      ? "disabled"
      : "default";

  const isLocked =
    isManualMode &&
    workflowStep.manualStatus === WORKFLOW_STEP_MANUAL_STATUS.LOCKED;

  const actions: ActionsProps[] = [];

  const canUpdateWorkflow = authorized({
    permission: "update",
    resource: RESOURCE_NAME.WORKFLOW,
  });

  const canShareWorkflow = authorized({
    permission: "share",
    resource: RESOURCE_NAME.WORKFLOW,
  });

  const [showUpdateStatusModal] = useUpdateWorkflowStepStatusModal({
    workflowStepId: workflowStep.id,
    workflowStepManualStatus: workflowStep.manualStatus,
    submissionTypeName: workflowStep.name,
    onUpdate: () => {
      // Refresh handled by refetchQueries in mutation
    },
  });

  const [showInsertWorkflowStepModal] = useInsertWorkflowStepModal({
    workflow,
    workflowStepDefinition: workflowStep.workflowStepDefinition,
    startingName: workflowStep.name,
    addAtOrderIndex: workflowStep.order + 1,
    clonedFromStepId: workflowStep.id,
  });

  const [showDeleteWorkflowStepModal] = useDeleteWorkflowStepModal({
    workflowStep,
    workflow,
  });

  const [showShareModal] = useShareWorkflowStepModal({
    workflowStep,
    workflow,
  });

  if (canUpdateWorkflow) {
    actions.push(
      { label: "Update status", onClick: showUpdateStatusModal },
      { label: "Duplicate", onClick: showInsertWorkflowStepModal },
    );

    if (workflowStep.clonedFromStep) {
      actions.push({
        label: "Delete",
        onClick: showDeleteWorkflowStepModal,
      });
    }
  }

  if (canShareWorkflow) {
    actions.push({ label: "Share", onClick: showShareModal });
  }

  return (
    <WorkflowStepContainer
      data-testid="workflow-step"
      data-interaction={interactionState}
      interaction={interactionState}
      onClick={onCardClick}
    >
      <FlexRow gap="s" style={{ alignItems: "center" }}>
        <Icon
          iconName="file-text"
          color="contentSecondary"
          size={15}
          style={{
            flexShrink: 0,
          }}
        />
        {stepName}
      </FlexRow>
      <FlexRow gap="l" style={{ alignItems: "center" }}>
        {isLocked ? (
          <Icon
            iconName="lock"
            color="contentSecondary"
            size={15}
            data-testid="workflow-step-lock-icon"
          />
        ) : (
          statusTagInfo && (
            <Tag
              styleVariant={statusTagInfo.variant}
              data-testid="workflow-step-status-tag"
            >
              {statusTagInfo.text}
            </Tag>
          )
        )}
        {isManualMode && arrayHasAtLeastOneItem(actions) && (
          <div onClick={e => e.stopPropagation()}>
            <Actions actions={actions} />
          </div>
        )}
      </FlexRow>
    </WorkflowStepContainer>
  );
};
